/**
 * TenantBillingTab — Vista Billing per tenant (Operator Console).
 *
 * Mostra al super_admin lo stato Stripe del tenant:
 *  - Stato subscription (active/trialing/past_due/canceled/free/exempt)
 *  - Tier corrente, seat usati/quota, MRR stimato
 *  - Stripe Customer ID + link diretto a Stripe Dashboard
 *  - Date trial / fine periodo / ultimo pagamento
 *  - Note exempt (perche' e' esente)
 *
 * Azioni rapide:
 *  - Toggle `billing.exempt` (per casi speciali: promo / partnership)
 *  - Toggle `billing.founding_partner` (−30% per sempre sui checkout futuri,
 *    via callable `setTenantFoundingPartner` — mai updateDoc diretto)
 *  - Editor "Posti per tier" (posti misti — solo exempt/pilota, scrive
 *    seat.quota_by_tier + seat.quota = somma)
 *  - Link a Stripe Dashboard del customer (apre nuova tab)
 *
 * Prezzi/etichette tier: SEMPRE da `tier_definitions` (fetch locale cacheata)
 * — mai hardcoded nel codice.
 *
 * Le modifiche al tier vanno fatte via callable `setTenantTier` (gia' esistente)
 * — non da qui, perche' setTenantTier ricalcola correttamente modules.base
 * con inclusione gerarchica.
 *
 * @since 2026-05-18 (MOD-5 console subscription view)
 * @updated 2026-07-18 (posti misti: breakdown per tier + editor exempt/pilota)
 * @updated 2026-07-19 (founding partner −30% per sempre + mirror sconto attivo)
 */

import React, { useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Paper, Chip, Button, Switch, FormControlLabel,
  TextField, Alert, alpha, CircularProgress, Divider,
} from '@mui/material';
import {
  OpenInNew, AccountBalance, Inventory, Schedule, Receipt, Edit, Save, PeopleAlt,
  WorkspacePremium, Science,
} from '@mui/icons-material';
import {
  deleteField, doc, Timestamp, updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';
import { tokens } from '../theme';
import type { TenantConsoleListItem } from '../services/tenantService';
import { SEAT_TIERS, useListiniDelCanone, useTierDefs } from '../services/listinoDeiPiani';
import { ePostoFuoriDallaScala } from '@shared/abbonamento/funzioniDelPosto';
import { canoneMensileDellAzienda, incassoDellAzienda } from '@shared/abbonamento/canoneMensile';
import { naturaDellAzienda } from '@shared/abbonamento/naturaAzienda';
import { DettaglioCanone, euroDaCentesimi, testoIncasso } from './canone/DettaglioCanone';
import type { SeatCountByTier, SeatTier } from '@shared/types';

const STATUS_COLOR_MAP: Record<string, string> = {
  active: tokens.color.success,
  trialing: tokens.color.info,
  past_due: tokens.color.warning,
  canceled: tokens.color.danger,
  unpaid: tokens.color.danger,
  free: tokens.color.ash,
  incomplete: tokens.color.warning,
  incomplete_expired: tokens.color.danger,
};

const STATUS_LABEL_MAP: Record<string, string> = {
  active: 'Attiva',
  trialing: 'In prova (trial)',
  past_due: 'Insoluto',
  canceled: 'Cancellata',
  unpaid: 'Non pagata',
  free: 'Gratuita',
  incomplete: 'Incompleta',
  incomplete_expired: 'Incompleta scaduta',
};

interface TenantBillingTabProps {
  tenant: TenantConsoleListItem;
  onChange: () => Promise<void> | void;
}

function formatDate(ts: { seconds?: number; nanoseconds?: number } | string | undefined): string {
  if (!ts) return '—';
  if (typeof ts === 'string') {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? ts : d.toLocaleString('it-IT');
  }
  if (typeof ts === 'object' && ts.seconds) {
    return new Date(ts.seconds * 1000).toLocaleString('it-IT');
  }
  return '—';
}

function stripeCustomerHref(customerId: string): string {
  return `https://dashboard.stripe.com/customers/${customerId}`;
}

function stripeSubscriptionHref(subscriptionId: string): string {
  return `https://dashboard.stripe.com/subscriptions/${subscriptionId}`;
}

export const TenantBillingTab: React.FC<TenantBillingTabProps> = ({ tenant, onChange }) => {
  const billing = tenant.billing ?? {};
  const tier = tenant.tier ?? 'base';
  const seatQuota = tenant.seat?.quota ?? 0;
  const seatUsed = tenant.seat?.used ?? 0;
  const quotaByTier = tenant.seat?.quota_by_tier;
  const usedByTier = tenant.seat?.used_by_tier;

  // Listino da tier_definitions (mai hardcoded)
  const { defs: tierDefs, error: tierDefsError } = useTierDefs('TenantBillingTab');
  // Il canone completo (posti + app a persona + strumenti a canone), la stessa regola dell'elenco delle aziende.
  const { listini, error: listiniError } = useListiniDelCanone('TenantBillingTab');
  const canone = useMemo(
    () => (listini ? canoneMensileDellAzienda(tenant, listini, Date.now()) : null),
    [tenant, listini],
  );
  const labelByTier = useMemo(() => {
    const map: Record<string, string> = {};
    for (const d of tierDefs ?? []) map[d.id] = d.label;
    return map;
  }, [tierDefs]);

  /** Tier con posti acquistati (posti misti), dal più alto al più basso; i posti fuori dalla scala (Presenze) in fondo. */
  const mixedTiers = useMemo<SeatTier[]>(() => {
    if (!quotaByTier) return [];
    const orderOf = (t: SeatTier) =>
      ePostoFuoriDallaScala(t) ? -1 : tierDefs?.find((d) => d.id === t)?.order ?? SEAT_TIERS.indexOf(t);
    return SEAT_TIERS
      .filter((t) => (quotaByTier[t] ?? 0) > 0)
      .sort((a, b) => orderOf(b) - orderOf(a));
  }, [quotaByTier, tierDefs]);
  const hasMixedSeats = mixedTiers.length > 0;

  const subscriptionStatus = billing.exempt
    ? 'exempt'
    : (billing.subscription_status ?? (tier === 'base' ? 'base' : 'incomplete'));

  const statusColor = subscriptionStatus === 'exempt'
    ? tokens.color.tierCustom.main
    : STATUS_COLOR_MAP[subscriptionStatus] ?? tokens.color.mist;

  const statusLabel = naturaDellAzienda(tenant) === 'dimostrativa'
    ? 'Azienda dimostrativa'
    : subscriptionStatus === 'exempt'
      ? 'Esente (no addebiti)'
      : STATUS_LABEL_MAP[subscriptionStatus] ?? subscriptionStatus;

  /** Se il canone entra davvero in cassa: «in prova fino al …», «pagamento in ritardo»… (null = paga, o esente). */
  const statoIncasso = useMemo(() => testoIncasso(incassoDellAzienda(tenant, Date.now())), [tenant]);

  const dimostrativa = naturaDellAzienda(tenant) === 'dimostrativa';

  /** Il canone al mese, IVA esclusa, in centesimi: null = listini non ancora letti (o errore). */
  const mrrCentesimi: number | null = billing.exempt || dimostrativa ? 0 : canone ? canone.totale_centesimi : null;

  return (
    <Stack spacing={2}>
      {/* HERO: stato subscription */}
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          borderColor: alpha(statusColor, 0.35),
          background: `linear-gradient(180deg, ${alpha(statusColor, 0.04)} 0%, ${tokens.color.paper} 70%)`,
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between" flexWrap="wrap">
          <Stack spacing={0.5}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: tokens.color.mist }}>
              Stato subscription
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: statusColor }} />
              <Typography sx={{ fontFamily: tokens.font.display, fontSize: '1.65rem', fontWeight: 600, color: tokens.color.graphite, lineHeight: 1.1 }}>
                {statusLabel}
              </Typography>
            </Stack>
            {billing.exempt_reason && (
              <Typography variant="caption" sx={{ color: tokens.color.ash, fontStyle: 'italic' }}>
                {billing.exempt_reason}
              </Typography>
            )}
          </Stack>

          <Stack spacing={0.5} alignItems="flex-end">
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: tokens.color.mist }}>
              MRR stimato
            </Typography>
            <Typography sx={{ fontFamily: tokens.font.mono, fontSize: '1.5rem', fontWeight: 700, color: tokens.color.graphite }}>
              {mrrCentesimi === null ? (listiniError ? '—' : '…') : euroDaCentesimi(mrrCentesimi)}
            </Typography>
            {statoIncasso && mrrCentesimi !== null && mrrCentesimi > 0 && (
              <Typography variant="caption" sx={{ color: tokens.color.warning, fontWeight: 600 }}>
                Non ancora incassato: {statoIncasso}
              </Typography>
            )}
            {hasMixedSeats ? (
              /* Posti misti: una riga "X/Y posti · tier" per ogni tier acquistato */
              <Stack spacing={0.25} alignItems="flex-end">
                {mixedTiers.map((t) => (
                  <Typography key={t} variant="caption" sx={{ color: tokens.color.mist, fontVariantNumeric: 'tabular-nums' }}>
                    {usedByTier?.[t] ?? 0}/{quotaByTier?.[t] ?? 0} posti · {labelByTier[t] ?? t}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                {seatUsed}/{seatQuota || '—'} seat · tier {tier}
              </Typography>
            )}
            {(tierDefsError || listiniError) && !billing.exempt && (
              <Typography variant="caption" sx={{ color: tokens.color.warning }}>
                listini non disponibili
              </Typography>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* CANONE MENSILE: voce per voce, quello che Stripe mette ogni mese nell'abbonamento */}
      {!billing.exempt && !dimostrativa && canone && canone.righe.length > 0 && (
        <Paper variant="outlined" sx={{ p: 3, borderColor: tokens.color.line }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Receipt fontSize="small" sx={{ color: tokens.color.ash }} />
            <Typography variant="subtitle2" fontWeight={700}>
              Canone mensile
            </Typography>
          </Stack>
          <Box sx={{ maxWidth: 560 }}>
            <DettaglioCanone canone={canone} />
          </Box>
        </Paper>
      )}

      {/* AZIENDA DIMOSTRATIVA: fuori dai conti dei clienti */}
      <NaturaControl tenant={tenant} onChange={onChange} />

      {/* AZIONE EXEMPT */}
      <ExemptControl tenant={tenant} onChange={onChange} />

      {/* FOUNDING PARTNER (−30% per sempre) */}
      <FoundingPartnerControl tenant={tenant} onChange={onChange} />

      {/* POSTI PER TIER (exempt/pilota) */}
      <SeatQuotaByTierControl tenant={tenant} onChange={onChange} labelByTier={labelByTier} />

      {/* DETTAGLI STRIPE */}
      <Paper variant="outlined" sx={{ p: 3, borderColor: tokens.color.line }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <AccountBalance fontSize="small" sx={{ color: tokens.color.ash }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Dettagli Stripe
          </Typography>
        </Stack>
        <Stack spacing={1.25}>
          <BillingRow label="Stripe Customer ID" value={billing.stripe_customer_id} mono link={billing.stripe_customer_id ? stripeCustomerHref(billing.stripe_customer_id) : undefined} />
          <BillingRow label="Stripe Subscription ID" value={billing.stripe_subscription_id} mono link={billing.stripe_subscription_id ? stripeSubscriptionHref(billing.stripe_subscription_id) : undefined} />
          <BillingRow label="Stripe Price ID corrente" value={billing.stripe_price_id} mono />
          <BillingRow label="Subscription status (raw)" value={billing.subscription_status} mono />
          <BillingRow
            label="Sconto attivo su subscription"
            value={
              billing.discount_percent != null
                ? `−${billing.discount_percent}%${billing.discount_forever ? ' per sempre' : ''}`
                : undefined
            }
          />
          <BillingRow label="Ha avuto subscription pagante" value={billing.has_had_paid_subscription ? 'Sì' : 'No'} />
        </Stack>
      </Paper>

      {/* TIMELINE */}
      <Paper variant="outlined" sx={{ p: 3, borderColor: tokens.color.line }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Schedule fontSize="small" sx={{ color: tokens.color.ash }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Timeline
          </Typography>
        </Stack>
        <Stack spacing={1.25}>
          <BillingRow label="Trial fino a" value={formatDate(billing.trial_ends_at)} />
          <BillingRow label="Ultimo pagamento" value={formatDate(billing.last_payment_at)} />
          <BillingRow label="Ultima fattura ID" value={billing.last_invoice_id} mono />
          <BillingRow label="Ultimo checkout session" value={billing.last_checkout_session_id} mono />
        </Stack>
      </Paper>

      {/* ESITO ULTIMO PAGAMENTO */}
      {billing.last_payment_status && (
        <Alert
          severity={
            billing.last_payment_status === 'succeeded' ? 'success' :
              billing.last_payment_status === 'failed' ? 'error' : 'info'
          }
          icon={<Receipt fontSize="small" />}
        >
          Ultimo esito pagamento: <strong>{billing.last_payment_status}</strong>
          {billing.last_payment_at && ` (${formatDate(billing.last_payment_at)})`}
        </Alert>
      )}
    </Stack>
  );
};

// ============================================================================
// EXEMPT CONTROL — toggle billing.exempt con reason
// ============================================================================

const ExemptControl: React.FC<TenantBillingTabProps> = ({ tenant, onChange }) => {
  const billing = tenant.billing ?? {};
  const [exempt, setExempt] = useState(!!billing.exempt);
  const [reason, setReason] = useState(billing.exempt_reason ?? '');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  const dirty = exempt !== !!billing.exempt || reason !== (billing.exempt_reason ?? '');

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'tenants', tenant.id), {
        'billing.exempt': exempt,
        'billing.exempt_reason': exempt ? reason.trim() : null,
        'billing.exempt_marked_at': exempt ? Timestamp.now() : null,
        updated_at: Timestamp.now(),
      });
      await onChange();
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, borderColor: tokens.color.line }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: editing ? 2 : 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Inventory fontSize="small" sx={{ color: tokens.color.ash }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Esenzione billing
          </Typography>
          {!editing && (
            <Chip
              label={billing.exempt ? 'ESENTE' : 'Pagante'}
              size="small"
              sx={{
                bgcolor: billing.exempt ? alpha(tokens.color.tierCustom.main, 0.12) : alpha(tokens.color.graphite, 0.08),
                color: billing.exempt ? tokens.color.tierCustom.main : tokens.color.graphite,
                fontWeight: 700,
                fontSize: '0.66rem',
                letterSpacing: '0.06em',
              }}
            />
          )}
        </Stack>
        {!editing && (
          <Button size="small" startIcon={<Edit fontSize="small" />} onClick={() => setEditing(true)}>
            Modifica
          </Button>
        )}
      </Stack>

      {editing && (
        <Stack spacing={1.5}>
          <FormControlLabel
            control={<Switch checked={exempt} onChange={(_, v) => setExempt(v)} />}
            label={exempt ? 'Esente da addebiti automatici Stripe' : 'Tenant pagante regolare'}
          />
          {exempt && (
            <TextField
              label="Motivo esenzione"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder="Es. tenant fondatore / partnership commerciale / promozione Black Friday"
            />
          )}
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" startIcon={<Save />} onClick={handleSave} disabled={!dirty || saving}>
              {saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Salva'}
            </Button>
            <Button size="small" onClick={() => {
              setExempt(!!billing.exempt);
              setReason(billing.exempt_reason ?? '');
              setEditing(false);
            }}>
              Annulla
            </Button>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
};

// ============================================================================
// AZIENDA DIMOSTRATIVA — `billing.natura` (02/11/2026)
// ============================================================================

/**
 * Segna un'azienda come dimostrativa (video, demo, prove del programmatore): non è un cliente e non lo sarà mai, quindi
 * esce da incassi, prove e conteggi della console (`naturaDellAzienda`). Il motivo è obbligatorio e resta scritto.
 * Scrive in `billing`, che le regole lasciano scrivere solo al super utente e al server, come l'esenzione qui sopra.
 */
const NaturaControl: React.FC<TenantBillingTabProps> = ({ tenant, onChange }) => {
  const billing = tenant.billing ?? {};
  const attuale = naturaDellAzienda(tenant) === 'dimostrativa';
  const [dimostrativa, setDimostrativa] = useState(attuale);
  const [motivo, setMotivo] = useState(billing.natura_motivo ?? '');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const dirty = dimostrativa !== attuale || (dimostrativa && motivo.trim() !== (billing.natura_motivo ?? ''));
  const motivoMancante = dimostrativa && motivo.trim() === '';

  const handleSave = async () => {
    setSaving(true);
    setErrore(null);
    try {
      await updateDoc(doc(db, 'tenants', tenant.id), {
        'billing.natura': dimostrativa ? 'dimostrativa' : 'cliente',
        'billing.natura_motivo': dimostrativa ? motivo.trim() : null,
        'billing.natura_aggiornata_il': Timestamp.now(),
        updated_at: Timestamp.now(),
      });
      console.info('[NaturaControl] natura aggiornata', { tenant: tenant.id, dimostrativa });
      await onChange();
      setEditing(false);
    } catch (e) {
      console.error('[NaturaControl] Errore salvataggio natura', e);
      setErrore(e instanceof Error ? e.message : 'Salvataggio non riuscito.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, borderColor: tokens.color.line }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: editing || attuale ? 1.5 : 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Science fontSize="small" sx={{ color: tokens.color.ash }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Azienda dimostrativa
          </Typography>
          {!editing && (
            <Chip
              label={attuale ? 'Sì' : 'No, è un cliente'}
              size="small"
              sx={{
                bgcolor: alpha(tokens.color.graphite, attuale ? 0.12 : 0.06),
                color: tokens.color.graphite,
                fontWeight: 700,
                fontSize: '0.66rem',
              }}
            />
          )}
        </Stack>
        {!editing && (
          <Button size="small" startIcon={<Edit fontSize="small" />} onClick={() => setEditing(true)}>
            Modifica
          </Button>
        )}
      </Stack>

      {!editing && attuale && billing.natura_motivo && (
        <Typography variant="caption" sx={{ color: tokens.color.ash, fontStyle: 'italic' }}>
          {billing.natura_motivo}
        </Typography>
      )}

      {editing && (
        <Stack spacing={1.5}>
          <FormControlLabel
            control={<Switch checked={dimostrativa} onChange={(_, v) => setDimostrativa(v)} />}
            label={dimostrativa
              ? 'Non è un cliente: fuori da incassi, prove e conteggi'
              : 'È un cliente (vero, in prova o esente)'}
          />
          {dimostrativa && (
            <TextField
              label="A cosa serve"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              size="small"
              fullWidth
              required
              error={motivoMancante}
              placeholder="Es. video e prove del programmatore"
            />
          )}
          {errore && <Alert severity="error">{errore}</Alert>}
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" startIcon={<Save />} onClick={handleSave} disabled={!dirty || motivoMancante || saving}>
              {saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Salva'}
            </Button>
            <Button size="small" onClick={() => {
              setDimostrativa(attuale);
              setMotivo(billing.natura_motivo ?? '');
              setErrore(null);
              setEditing(false);
            }}>
              Annulla
            </Button>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
};

// ============================================================================
// FOUNDING PARTNER CONTROL — sconto −30% per sempre (callable super_admin)
// ============================================================================

/**
 * Toggle `billing.founding_partner` via callable `setTenantFoundingPartner`
 * (super_admin only, con audit immutabile lato server — MAI updateDoc diretto:
 * il flag pilota lo sconto Stripe e deve passare dal percorso auditato).
 *
 * Effetto: ogni futura Checkout Session del tenant applica in automatico il
 * coupon Stripe −30% per sempre al posto del campo codici promozionali
 * (vincolo Stripe: `discounts` e `allow_promotion_codes` sono incompatibili).
 * Non modifica la subscription già attiva.
 */
const FoundingPartnerControl: React.FC<TenantBillingTabProps> = ({ tenant, onChange }) => {
  const active = tenant.billing?.founding_partner === true;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = async (next: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'setTenantFoundingPartner');
      await fn({ tenant_id: tenant.id, founding_partner: next });
      await onChange();
    } catch (e) {
      console.error('[FoundingPartnerControl] Errore aggiornamento flag founding partner', e);
      setError(e instanceof Error ? e.message : 'Errore aggiornamento flag founding partner.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, borderColor: tokens.color.line }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" rowGap={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <WorkspacePremium fontSize="small" sx={{ color: tokens.color.ash }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Founding partner (−30% per sempre)
          </Typography>
          <Chip
            label={active ? 'ATTIVO' : 'Non attivo'}
            size="small"
            sx={{
              bgcolor: active ? alpha(tokens.color.tierCustom.main, 0.12) : alpha(tokens.color.graphite, 0.08),
              color: active ? tokens.color.tierCustom.main : tokens.color.graphite,
              fontWeight: 700,
              fontSize: '0.66rem',
              letterSpacing: '0.06em',
            }}
          />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          {saving && <CircularProgress size={16} />}
          <Switch
            checked={active}
            disabled={saving}
            onChange={(_, v) => { void handleToggle(v); }}
            inputProps={{ 'aria-label': 'Founding partner (−30% per sempre)' }}
          />
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}

      <Typography variant="caption" sx={{ color: tokens.color.mist, display: 'block', mt: 1 }}>
        Con il flag attivo ogni futura sessione di checkout applica in automatico il coupon
        Stripe −30% per sempre (al posto del campo codici promozionali). Non modifica la
        subscription già attiva. Modifica auditata via callable setTenantFoundingPartner.
      </Typography>
    </Paper>
  );
};

// ============================================================================
// SEAT QUOTA BY TIER CONTROL — editor posti per tier (exempt/pilota)
// ============================================================================

interface SeatQuotaByTierControlProps extends TenantBillingTabProps {
  /** Etichette tier da `tier_definitions` (fallback: id tier). */
  labelByTier: Record<string, string>;
}

/** Fallback etichetta tier quando `tier_definitions` non è caricato. */
function fallbackTierLabel(tier: SeatTier): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

/**
 * Editor console dei posti per tier (posti misti, 2026-07-18).
 *
 * Pensato per tenant `billing.exempt` e piloti (es. Founding Partner): scrive
 * DIRETTAMENTE `tenants/{id}.seat.quota_by_tier` + `seat.quota` = somma via
 * updateDoc (la console è solo super_admin — le rules bloccano chiunque altro).
 * I tenant paganti ricevono invece la quota dal webhook Stripe: per loro
 * questo editor va usato solo in casi eccezionali (warning in UI).
 *
 * Tutti a 0 = rimuove `quota_by_tier` (il tenant torna alla quota singola
 * legacy; `seat.quota` NON viene toccato in quel caso).
 */
const SeatQuotaByTierControl: React.FC<SeatQuotaByTierControlProps> = ({ tenant, onChange, labelByTier }) => {
  const quotaByTier: SeatCountByTier = tenant.seat?.quota_by_tier ?? {};
  const usedByTier: SeatCountByTier = tenant.seat?.used_by_tier ?? {};
  const configured = SEAT_TIERS.some((t) => (quotaByTier[t] ?? 0) > 0);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valoriDi = (q: SeatCountByTier) =>
    Object.fromEntries(SEAT_TIERS.map((t) => [t, String(q[t] ?? 0)])) as Record<SeatTier, string>;
  const [values, setValues] = useState<Record<SeatTier, string>>(() => valoriDi(quotaByTier));

  const startEditing = () => {
    setValues(valoriDi(quotaByTier));
    setError(null);
    setEditing(true);
  };

  /** Parse dei campi: '' = 0; non-intero o negativo = null (invalido). */
  const parsed = useMemo(() => {
    const map: Partial<Record<SeatTier, number | null>> = {};
    for (const t of SEAT_TIERS) {
      const raw = values[t].trim();
      if (raw === '') { map[t] = 0; continue; }
      const n = Number(raw);
      map[t] = Number.isInteger(n) && n >= 0 ? n : null;
    }
    return map;
  }, [values]);

  // Un posto fuori dalla scala (Presenze) non si compra da solo: serve almeno un posto Base, Pro o Business (la stessa
  // regola del server, `verificaAcquistoDeiPosti`).
  const totaleScala = SEAT_TIERS.filter((t) => !ePostoFuoriDallaScala(t)).reduce((sum, t) => sum + (parsed[t] ?? 0), 0);
  const total = SEAT_TIERS.reduce((sum, t) => sum + (parsed[t] ?? 0), 0);
  const senzaPostoDellaScala = total > 0 && totaleScala === 0;
  const invalid = SEAT_TIERS.some((t) => parsed[t] === null) || senzaPostoDellaScala;
  const belowUsed = SEAT_TIERS.filter((t) => (parsed[t] ?? 0) < (usedByTier[t] ?? 0));

  const dirty = SEAT_TIERS.some((t) => (parsed[t] ?? 0) !== (quotaByTier[t] ?? 0));

  const handleSave = async () => {
    if (invalid) return;
    setSaving(true);
    setError(null);
    try {
      // Chiavi solo > 0 (semantica SeatCountByTier: chiave assente = 0)
      const clean: SeatCountByTier = {};
      for (const t of SEAT_TIERS) {
        const n = parsed[t] ?? 0;
        if (n > 0) clean[t] = n;
      }
      const now = Timestamp.now();
      const actorUid = auth.currentUser?.uid ?? null;
      if (Object.keys(clean).length === 0) {
        // Rimozione configurazione posti misti → torna a quota singola legacy
        await updateDoc(doc(db, 'tenants', tenant.id), {
          'seat.quota_by_tier': deleteField(),
          'seat.quota_updated_at': now,
          'seat.quota_updated_by_uid': actorUid,
          updated_at: now,
        });
      } else {
        await updateDoc(doc(db, 'tenants', tenant.id), {
          'seat.quota_by_tier': clean,
          'seat.quota': total,
          'seat.quota_updated_at': now,
          'seat.quota_updated_by_uid': actorUid,
          updated_at: now,
        });
      }
      await onChange();
      setEditing(false);
    } catch (e) {
      console.error('[SeatQuotaByTierControl] Errore salvataggio posti per tier', e);
      setError(e instanceof Error ? e.message : 'Errore salvataggio posti per tier.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, borderColor: tokens.color.line }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: editing ? 2 : 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PeopleAlt fontSize="small" sx={{ color: tokens.color.ash }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Posti per tier (exempt/pilota)
          </Typography>
          {!editing && (
            configured ? (
              <Stack direction="row" spacing={0.5}>
                {SEAT_TIERS.filter((t) => (quotaByTier[t] ?? 0) > 0).map((t) => (
                  <Chip
                    key={t}
                    label={`${labelByTier[t] ?? fallbackTierLabel(t)} ${quotaByTier[t]}`}
                    size="small"
                    sx={{
                      bgcolor: alpha(tokens.color.tierPro.main, 0.08),
                      color: tokens.color.tierPro.main,
                      fontWeight: 700,
                      fontSize: '0.66rem',
                    }}
                  />
                ))}
              </Stack>
            ) : (
              <Chip
                label="Non configurato"
                size="small"
                sx={{
                  bgcolor: alpha(tokens.color.graphite, 0.08),
                  color: tokens.color.mist,
                  fontWeight: 700,
                  fontSize: '0.66rem',
                  letterSpacing: '0.06em',
                }}
              />
            )
          )}
        </Stack>
        {!editing && (
          <Button size="small" startIcon={<Edit fontSize="small" />} onClick={startEditing}>
            Modifica
          </Button>
        )}
      </Stack>

      {!editing && (
        <Typography variant="caption" sx={{ color: tokens.color.mist, display: 'block', mt: 1 }}>
          {configured
            ? `Somma posti = ${SEAT_TIERS.reduce((s, t) => s + (quotaByTier[t] ?? 0), 0)} (allineata a seat.quota).`
            : 'Il tenant usa la quota singola legacy (seat.quota). Configura qui i posti per tier solo per esenti/piloti.'}
        </Typography>
      )}

      {editing && (
        <Stack spacing={1.5}>
          {!tenant.billing?.exempt && (
            <Alert severity="warning">
              Tenant pagante: la quota per tier viene riscritta dal webhook Stripe al prossimo
              evento subscription. Usa questo editor solo per esenti o casi eccezionali.
            </Alert>
          )}
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            {SEAT_TIERS.map((t) => (
              <TextField
                key={t}
                label={labelByTier[t] ?? fallbackTierLabel(t)}
                value={values[t]}
                onChange={(e) => setValues((prev) => ({ ...prev, [t]: e.target.value }))}
                size="small"
                type="number"
                inputProps={{ min: 0, step: 1, inputMode: 'numeric' }}
                error={parsed[t] === null}
                helperText={parsed[t] === null ? 'Intero >= 0' : ' '}
                sx={{ maxWidth: 140 }}
              />
            ))}
          </Stack>

          {senzaPostoDellaScala && (
            <Alert severity="error">
              I posti Presenze non bastano da soli: serve almeno un posto Base, Pro o Business.
            </Alert>
          )}

          {belowUsed.length > 0 && (
            <Alert severity="warning">
              Sotto i posti occupati:{' '}
              {belowUsed
                .map((t) => `${labelByTier[t] ?? fallbackTierLabel(t)} ha ${usedByTier[t] ?? 0} occupati`)
                .join(' · ')}
              . Salvando, gli utenti in eccesso restano attivi ma il tenant risulta oltre quota.
            </Alert>
          )}

          <Typography variant="caption" sx={{ color: tokens.color.mist }}>
            Scrive direttamente tenants.seat.quota_by_tier e seat.quota = somma ({total}).
            Tutti a 0 = rimuove la configurazione posti misti (torna alla quota singola;
            seat.quota resta invariato).
          </Typography>

          <Divider sx={{ my: 1 }} />
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={!dirty || invalid || saving}
            >
              {saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Salva'}
            </Button>
            <Button size="small" onClick={() => setEditing(false)} disabled={saving}>
              Annulla
            </Button>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
};

// ============================================================================
// HELPER ROW
// ============================================================================

interface BillingRowProps {
  label: string;
  value: string | number | boolean | null | undefined;
  mono?: boolean;
  link?: string;
}

const BillingRow: React.FC<BillingRowProps> = ({ label, value, mono, link }) => {
  const display = value === undefined || value === null || value === ''
    ? '—'
    : typeof value === 'boolean' ? (value ? 'Sì' : 'No') : String(value);
  const missing = display === '—';
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 2 }} alignItems={{ sm: 'center' }}>
      <Typography variant="caption" sx={{ minWidth: 200, color: tokens.color.mist, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography
          sx={{
            fontFamily: mono ? tokens.font.mono : tokens.font.body,
            fontSize: '0.85rem',
            color: missing ? tokens.color.mist : tokens.color.graphite,
            wordBreak: 'break-all',
          }}
        >
          {display}
        </Typography>
        {link && !missing && (
          <a href={link} target="_blank" rel="noreferrer noopener" style={{ display: 'inline-flex' }}>
            <OpenInNew sx={{ fontSize: 14, color: tokens.color.tierPro.main }} />
          </a>
        )}
      </Stack>
    </Stack>
  );
};

export default TenantBillingTab;
